import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Grid2 as Grid,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { DataGrid } from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@emotion/react";
import { alpha } from "@mui/system";
import axios from "axios";
import validator from "validator";
import { handlePhoneInputChange as formatPhone } from "../../functions/permitFormHelpers";
import ContractorCard from "../permitComponents/ContractorCard";
import EnterpriseNotariesGrid from "../enterprise/EnterpriseNotariesGrid";

const TabPanel = ({ children, value, index }) => {
  if (value !== index) return null;

  return <Box sx={{ pt: 3 }}>{children}</Box>;
};

const OverviewMetricCard = ({ label, value, helper }) => (
  <Card sx={{ height: "100%", borderRadius: 3, boxShadow: 3 }}>
    <CardContent>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
      {!!helper && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {helper}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const BranchLicensePreviewCard = ({
  license,
  poaName,
  poaEmail,
  poaPhone,
  selected,
  onToggle,
  colorIndex = 0,
}) => {
  const colors = [
    "#3f51b5",
    "#f44336",
    "#4caf50",
    "#ff9800",
    "#9c27b0",
    "#673ab7",
    "#009688",
    "#795548",
    "#1e88e5",
    "#d32f2f",
  ];

  const avatarColor = colors[colorIndex % colors.length] || colors[0];
  const initials =
    poaName
      ?.split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase() || "PO";

  return (
    <Card
      sx={{
        maxWidth: 400,
        margin: "auto",
        boxShadow: 6,
        borderRadius: 3,
        border: "3px solid",
        borderColor: selected ? "primary.main" : alpha("#000000", 0.1),
        backgroundColor: selected ? alpha("#1976d2", 0.06) : undefined,
        position: "relative",
      }}
    >
      <CardContent sx={{ position: "relative" }}>
        <Box sx={{ position: "absolute", top: 12, left: 12, zIndex: 2 }}>
          <Checkbox checked={selected} onChange={onToggle} />
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }} sx={{ textAlign: "center" }}>
            <Avatar
              sx={{
                bgcolor: avatarColor,
                width: 80,
                height: 80,
                margin: "auto",
                mb: 2,
              }}
            >
              <Typography variant="h5">{initials}</Typography>
            </Avatar>

            {license?.status && (
              <Box
                sx={{
                  position: "absolute",
                  top: 16,
                  left: 56,
                  borderRadius: 1,
                  padding: "0.5rem",
                }}
              >
                <Typography
                  sx={{ cursor: "default" }}
                  variant="h6"
                  color={license?.status === "Inactive" ? "red" : "#4caf50"}
                >
                  {license?.status}
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="primary">
                  {license?.expires}
                </Typography>
              </Box>
            )}

            {license?.rank && (
              <Box
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  backgroundColor: alpha("#000000", 0.08),
                  borderRadius: 1,
                  padding: "0.5rem 0.75rem",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {license?.rank}
                </Typography>
              </Box>
            )}

            <Typography variant="h5" component="div" sx={{ fontWeight: 700 }}>
              {license?.businessName || "Company License"}
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mt: 0.5, fontSize: "0.95rem" }}
            >
              {poaName || "POA Name"}
            </Typography>

            <Typography
              sx={{ cursor: "default", mt: 1.5 }}
              variant="body1"
              fontWeight="bold"
              color="textSecondary"
            >
              {license?.licenseType}
            </Typography>

            <Typography
              sx={{ cursor: "default", mt: 0 }}
              variant="h6"
              color="textSecondary"
            >
              {license?.licenseNumber}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }} sx={{ textAlign: "left", mt: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <LocationOnIcon sx={{ mr: 1 }} />
              <Typography variant="body2" color="textSecondary">
                {license?.mainAddress || ""}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <LocationOnIcon sx={{ mr: 1, visibility: "hidden" }} />
              <Typography variant="body2" color="textSecondary">
                {license?.mainAddressCounty || ""}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <EmailIcon sx={{ mr: 1 }} />
              <Typography variant="body2" color="textSecondary">
                {poaEmail || ""}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <PhoneIcon sx={{ mr: 1 }} />
              <Typography variant="body2" color="textSecondary">
                {poaPhone || ""}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const Branches = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]?.user);

  const mobileSize = useMediaQuery(theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery(theme.breakpoints.down("md"));
  const largePC = useMediaQuery(theme.breakpoints.up("xl"));

  const isEnterprise = Boolean(currentUser?.user?.enterprise?.is_enterprise);
  const [activeTab, setActiveTab] = useState(0);
  const [createBranchLoading, setCreateBranchLoading] = useState(false);
  const [branchFormErrors, setBranchFormErrors] = useState({});
  const [branchRequestError, setBranchRequestError] = useState("");
  const [branchRequestSuccess, setBranchRequestSuccess] = useState("");
  const [branchUsers, setBranchUsers] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchDetailsOpen, setBranchDetailsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [branchToDelete, setBranchToDelete] = useState(null);
  const [deleteBranchLoading, setDeleteBranchLoading] = useState(false);
  const [branchForm, setBranchForm] = useState({
    branchName: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    licenseType: "",
    licenseNumber: "",
    businessName: "",
  });

  useEffect(() => {
    if (!isEnterprise) {
      navigate("/login");
    }
  }, [isEnterprise, navigate]);

  const personal = currentUser?.user?.personal?.[0] || {};
  const plan = currentUser?.tier || currentUser?.user?.plan || {};
  const enterpriseMeta = currentUser?.user?.enterprise || {};
  const analytics = currentUser?.user?.analytics || {};
  const selectedCounties = currentUser?.user?.selectedCountiesNOC || [];
  const enterpriseLicenses = useMemo(
    () => currentUser?.user?.contractors ?? [],
    [currentUser?.user?.contractors],
  );
  const hasCompanyLicenses = enterpriseLicenses.length > 0;
  const [selectedLicenseKeys, setSelectedLicenseKeys] = useState([]);
  const [licensePage, setLicensePage] = useState(0);

  const initialBranchUsers = useMemo(() => {
    const enterpriseBranches = currentUser?.user?.enterprise?.branches;
    const rootBranches = currentUser?.user?.branches;

    if (Array.isArray(enterpriseBranches)) return enterpriseBranches;
    if (Array.isArray(rootBranches)) return rootBranches;
    return [];
  }, [currentUser]);

  useEffect(() => {
    setBranchUsers(initialBranchUsers);
  }, [initialBranchUsers]);

  const fetchBranches = async () => {
    const response = await axios.post(
      `${process.env.REACT_APP_BASE_URL}/nocApi/enterprise/branches/list`,
      { currentSession },
      { withCredentials: true },
    );

    if (response?.data?.success) {
      setBranchUsers(response.data.data || []);
    }
  };

  const handleOpenBranchDetails = (branch) => {
    setSelectedBranch(branch);
    setBranchDetailsOpen(true);
  };

  const handleRequestDeleteBranch = (branch) => {
    setBranchToDelete(branch);
    setDeleteDialogOpen(true);
  };

  const handleDeleteBranch = async () => {
    if (!branchToDelete?.email) return;

    try {
      setDeleteBranchLoading(true);
      setBranchRequestError("");

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/enterprise/branches/delete`,
        {
          currentSession,
          email: branchToDelete.email,
        },
        { withCredentials: true },
      );

      if (response?.data?.success) {
        setBranchUsers(response.data.data || []);
        setDeleteDialogOpen(false);
        if (selectedBranch?.email === branchToDelete.email) {
          setSelectedBranch(null);
          setBranchDetailsOpen(false);
        }
        setBranchToDelete(null);
        setBranchRequestSuccess("Branch removed successfully.");
      }
    } catch (error) {
      setBranchRequestError(
        error?.response?.data?.message || "Failed to delete branch user.",
      );
    } finally {
      setDeleteBranchLoading(false);
    }
  };

  useEffect(() => {
    if (isEnterprise) {
      fetchBranches().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnterprise]);

  const overviewMetrics = [
    {
      label: "Enterprise ID",
      value: enterpriseMeta?.enterprise_id || "N/A",
      helper: "Primary organization identifier",
    },
    {
      label: "Branch Count",
      value: branchUsers.length,
      helper: "Active linked branch accounts",
    },
    {
      label: "License Allowance",
      value: plan?.license || analytics?.contractors_limit || "N/A",
      helper: "Total company licenses available",
    },
    {
      label: "County Access",
      value: selectedCounties.length,
      helper: "Counties currently available to the organization",
    },
    {
      label: "Downloads",
      value: plan?.downloads || analytics?.availableDownloads || "N/A",
      helper: "Plan download allowance",
    },
    {
      label: "Base Plan",
      value: plan?.title || currentUser?.user?.plan?.title || "Enterprise",
      helper: "Current enterprise plan",
    },
  ];

  const branchGridRows = useMemo(
    () =>
      branchUsers.map((branch, index) => ({
        id: branch?.branch_id || branch?.email || index,
        branchName:
          branch?.branch_name ||
          branch?.additional?.companyName ||
          `${branch?.personal?.[0]?.firstName || ""} ${branch?.personal?.[0]?.lastName || ""}`.trim() ||
          branch?.email ||
          `Branch ${index + 1}`,
        email: branch?.email || "",
        phone: branch?.personal?.[0]?.phone || "N/A",
        createdAt: branch?.createdAt
          ? new Date(branch.createdAt * 1000).toLocaleDateString()
          : "N/A",
        raw: branch,
      })),
    [branchUsers],
  );

  const branchColumns = useMemo(
    () => [
      {
        field: "branchName",
        headerName: "Branch Name",
        flex: 1.4,
        minWidth: 150,
      },
      { field: "email", headerName: "Email", flex: 1.6, minWidth: 170 },
      { field: "phone", headerName: "Phone", flex: 1, minWidth: 140 },
      { field: "createdAt", headerName: "Created", flex: 0.9, minWidth: 90 },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        filterable: false,
        width: 80,
        minWidth: 80,
        renderCell: (params) => (
          <IconButton
            color="error"
            onClick={(event) => {
              event.stopPropagation();
              handleRequestDeleteBranch(params.row.raw);
            }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        ),
      },
    ],
    [],
  );

  const handleBranchFormChange = (event) => {
    const { name, value } = event.target;
    setBranchForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getLicenseKey = (license, index) => {
    if (license?.licenseNumber) return String(license.licenseNumber);
    if (license?._id?.$oid) return String(license._id.$oid);
    if (license?._id) return String(license._id);
    if (license?.id) return String(license.id);
    if (license?.businessName) return String(license.businessName);
    return String(index);
  };

  useEffect(() => {
    const allKeys = enterpriseLicenses.map((license, index) =>
      getLicenseKey(license, index),
    );
    setSelectedLicenseKeys(allKeys);
    setLicensePage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enterpriseLicenses.length]);

  const selectedLicenses = useMemo(
    () =>
      enterpriseLicenses.filter((license, index) =>
        selectedLicenseKeys.includes(getLicenseKey(license, index)),
      ),
    [enterpriseLicenses, selectedLicenseKeys],
  );

  const currentSelectedLicense = selectedLicenses[licensePage] || null;
  const poaName =
    `${branchForm.firstName || ""} ${branchForm.lastName || ""}`.trim();
  const phoneDigits = branchForm.phone.replace(/\D/g, "");
  const isBranchFormComplete = Boolean(
    branchForm.branchName?.trim() &&
      branchForm.firstName?.trim() &&
      branchForm.lastName?.trim() &&
      branchForm.email?.trim() &&
      branchForm.phone?.trim() &&
      validator.isEmail(branchForm.email || "") &&
      phoneDigits.length === 10 &&
      selectedLicenseKeys.length > 0,
  );

  const validateBranchForm = () => {
    const nextErrors = {};

    if (!branchForm.branchName?.trim())
      nextErrors.branchName = "Branch name is required";
    if (!branchForm.firstName?.trim())
      nextErrors.firstName = "First name is required";
    if (!branchForm.lastName?.trim())
      nextErrors.lastName = "Last name is required";
    if (!branchForm.email?.trim()) nextErrors.email = "Email is required";
    else if (!validator.isEmail(branchForm.email))
      nextErrors.email = "Email must be valid";
    if (!branchForm.phone?.trim()) nextErrors.phone = "Phone is required";
    else if (phoneDigits.length !== 10)
      nextErrors.phone = "Phone must be a valid 10-digit mobile number";
    if (selectedLicenseKeys.length === 0)
      nextErrors.licenses = "Select at least one company license";

    setBranchFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleCreateBranch = async () => {
    setBranchRequestError("");
    setBranchRequestSuccess("");

    if (!validateBranchForm()) return;

    try {
      setCreateBranchLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/enterprise/branches/create`,
        {
          currentSession,
          values: {
            ...branchForm,
            email: branchForm.email.trim().toLowerCase(),
            selectedLicenseKeys,
          },
        },
        { withCredentials: true },
      );

      if (response?.data?.success) {
        setBranchUsers(response.data.data || []);
        setBranchForm({
          branchName: "",
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          licenseType: "",
          licenseNumber: "",
          businessName: "",
        });
        setBranchFormErrors({});
        handleSelectAllLicenses();
        setBranchRequestSuccess("Branch invitation sent successfully.");
        setActiveTab(1);
      }
    } catch (error) {
      setBranchRequestError(
        error?.response?.data?.message || "Failed to create branch user.",
      );
    } finally {
      setCreateBranchLoading(false);
    }
  };

  const handleToggleLicense = (license, index) => {
    const key = getLicenseKey(license, index);
    setSelectedLicenseKeys((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key],
    );
    setLicensePage(0);
  };

  const handleSelectAllLicenses = () => {
    setSelectedLicenseKeys(
      enterpriseLicenses.map((license, index) => getLicenseKey(license, index)),
    );
    setLicensePage(0);
  };

  const handleClearLicenseSelection = () => {
    setSelectedLicenseKeys([]);
    setLicensePage(0);
  };

  useEffect(() => {
    if (licensePage > Math.max(selectedLicenses.length - 1, 0)) {
      setLicensePage(0);
    }
  }, [licensePage, selectedLicenses.length]);

  if (!isEnterprise) {
    return null;
  }

  return (
    <Box
      sx={{
        mx: largePC ? "auto" : 0,
        maxWidth: "1700px",
        py: 1,
        backgroundColor: "#EEF2F6",
        overflowX: "hidden",
        mb: tabletSize ? "5rem" : undefined,
        mt: 1,
      }}
    >
      <Grid
        container
        sx={{
          px: mobileSize ? 1 : tabletSize ? 2 : 3,
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: "1500px",
            mx: "auto",
            p: mobileSize ? 2 : 3,
            borderRadius: 3,
          }}
        >
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Typography variant="h4" color="primary" gutterBottom>
              Branches
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 900, mx: "auto" }}
            >
              Manage your enterprise organization, review linked branches, and
              prepare new branch accounts that will share company access and
              licenses.
            </Typography>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              variant="fullWidth"
            >
              <Tab label="Enterprise Overview" />
              <Tab label={`Branches (${branchUsers.length})`} />
              <Tab label="Notaries" />
              <Tab label="Add Branch" />
            </Tabs>
          </Box>

          <TabPanel value={activeTab} index={0}>
            <Grid container spacing={2.5}>
              {overviewMetrics.map((metric) => (
                <Grid key={metric.label} size={{ xs: 12, sm: 6, lg: 4 }}>
                  <OverviewMetricCard {...metric} />
                </Grid>
              ))}

              <Grid size={{ xs: 12, lg: 7 }}>
                <Card sx={{ height: "100%", borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Enterprise Account Details
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          Account Holder
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {(personal?.firstName || "").trim()}{" "}
                          {(personal?.lastName || "").trim()}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {currentUser?.user?.email || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          Phone
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {personal?.phone || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          Timezone
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {personal?.timezone || "N/A"}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Box
                      sx={{ mt: 3, display: "flex", flexWrap: "wrap", gap: 1 }}
                    >
                      <Chip label="Enterprise Owner" color="primary" />
                      <Chip
                        label={`${selectedCounties.length} Counties Enabled`}
                        variant="outlined"
                      />
                      <Chip
                        label={`${enterpriseLicenses.length} Company Licenses`}
                        variant="outlined"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, lg: 5 }}>
                <Card sx={{ height: "100%", borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Shared Enterprise Access
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      All branches created under this enterprise account are
                      expected to inherit organization-level access, selected
                      counties, and shared company licenses.
                    </Typography>

                    <Typography variant="subtitle2" gutterBottom>
                      Current shared counties preview
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {selectedCounties.slice(0, 10).map((county) => (
                        <Chip
                          key={county?.name}
                          label={county?.name || "County"}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ))}
                      {selectedCounties.length > 10 && (
                        <Chip
                          label={`+${selectedCounties.length - 10} more`}
                          size="small"
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            {branchUsers.length === 0 ? (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }} sx={{ order: { xs: 2, lg: 2 } }}>
                  <Card sx={{ width: "100%", borderRadius: 3, boxShadow: 2 }}>
                    <CardContent>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        No branches have been linked yet.
                      </Alert>
                      <Typography
                        sx={{ textAlign: "center" }}
                        color="text.secondary"
                      >
                        Once branch accounts are created, they will appear here
                        in a management grid.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12 }} sx={{ order: { xs: 1, lg: 1 } }}>
                  <Card sx={{ height: "100%", borderRadius: 3, boxShadow: 3 }}>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Branch Summary
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        When branch accounts are created, this panel will show a
                        quick overview of branch activity and status.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }} sx={{ order: { xs: 2, lg: 2 } }}>
                  <Card sx={{ width: "100%", borderRadius: 3, boxShadow: 3 }}>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Branch Management
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Click a row to view branch details. Delete permanently
                        removes the branch from Stripe and the database.
                      </Typography>
                      <Box
                        sx={{
                          width: "100%",
                          minHeight: 420,
                          maxWidth: "100%",
                          overflowX: "hidden",
                        }}
                      >
                        <DataGrid
                          rows={branchGridRows}
                          columns={branchColumns}
                          disableRowSelectionOnClick
                          disableColumnMenu
                          disableColumnResize
                          pageSizeOptions={[5, 10, 25]}
                          initialState={{
                            pagination: {
                              paginationModel: { pageSize: 5, page: 0 },
                            },
                          }}
                          onRowClick={(params) =>
                            handleOpenBranchDetails(params.row.raw)
                          }
                          sx={{
                            width: "100%",
                            maxWidth: "100%",
                            border: "1px solid #FCF2F0",
                            overflowX: "hidden",
                            "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within":
                              {
                                outline: "none !important",
                              },
                            "& .MuiDataGrid-columnHeader:focus-within": {
                              outline: "none !important",
                            },
                            "& .MuiDataGrid-columnHeaders": { padding: 0 },
                            "& .MuiDataGrid-columnHeadersInner": {
                              width: "100%",
                            },
                            "& .MuiDataGrid-cell": { borderColor: "#333333" },
                            "& .MuiDataGrid-columnSeparator": {
                              display: "none",
                            },
                            "& .MuiDataGrid-row": { cursor: "pointer" },
                            "& .MuiDataGrid-main": {
                              overflowX: "hidden",
                            },
                            "& .MuiDataGrid-virtualScroller": {
                              overflowX: "hidden !important",
                            },
                            "& .MuiDataGrid-footerContainer": {
                              overflowX: "hidden",
                            },
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12 }} sx={{ order: { xs: 1, lg: 1 } }}>
                  <Card sx={{ height: "100%", borderRadius: 3, boxShadow: 3 }}>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Branch Summary
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            Total Branches
                          </Typography>
                          <Typography
                            variant="h5"
                            color="primary"
                            sx={{ fontWeight: 700 }}
                          >
                            {branchUsers.length}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            Pending Invites
                          </Typography>
                          <Typography
                            variant="h5"
                            color="primary"
                            sx={{ fontWeight: 700 }}
                          >
                            {
                              branchUsers.filter((branch) => branch?.firstLogin)
                                .length
                            }
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            Active Branches
                          </Typography>
                          <Typography
                            variant="h5"
                            color="primary"
                            sx={{ fontWeight: 700 }}
                          >
                            {
                              branchUsers.filter(
                                (branch) => !branch?.firstLogin,
                              ).length
                            }
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            Shared Licenses
                          </Typography>
                          <Typography
                            variant="h5"
                            color="primary"
                            sx={{ fontWeight: 700 }}
                          >
                            {branchUsers.reduce(
                              (total, branch) =>
                                total + (branch?.contractors?.length || 0),
                              0,
                            )}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Box
                        sx={{
                          mt: 3,
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                        }}
                      >
                        <Chip
                          label={`${branchUsers.length} Branches`}
                          color="primary"
                        />
                        <Chip
                          label={`${branchUsers.filter((branch) => branch?.firstLogin).length} Pending`}
                          variant="outlined"
                        />
                        <Chip
                          label={`${branchUsers.filter((branch) => !branch?.firstLogin).length} Active`}
                          variant="outlined"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <EnterpriseNotariesGrid
                  currentSession={currentSession}
                  currentUser={currentUser}
                />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, lg: 7 }}>
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Add New Branch
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      Create a new branch user under this enterprise account.
                      These branch users can sign on behalf of the company using
                      their POA (Power of Attorney) with the company licenses
                      you choose below.
                    </Typography>

                    {!hasCompanyLicenses && (
                      <>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          You need to add at least one Company License before
                          you can create a new branch.
                        </Alert>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            mb: 3,
                          }}
                        >
                          <Button
                            variant="contained"
                            color="warning"
                            onClick={() => navigate("/contractors")}
                          >
                            Go to Licenses
                          </Button>
                        </Box>
                      </>
                    )}

                    <Grid container spacing={2}>
                      {!!branchRequestError && (
                        <Grid size={{ xs: 12 }}>
                          <Alert severity="error">{branchRequestError}</Alert>
                        </Grid>
                      )}
                      {!!branchRequestSuccess && (
                        <Grid size={{ xs: 12 }}>
                          <Alert severity="success">
                            {branchRequestSuccess}
                          </Alert>
                        </Grid>
                      )}
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          label="Branch Name"
                          name="branchName"
                          value={branchForm.branchName}
                          onChange={handleBranchFormChange}
                          error={Boolean(branchFormErrors.branchName)}
                          helperText={branchFormErrors.branchName || ""}
                          disabled={!hasCompanyLicenses}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          label="First Name"
                          name="firstName"
                          value={branchForm.firstName}
                          onChange={handleBranchFormChange}
                          error={Boolean(branchFormErrors.firstName)}
                          helperText={branchFormErrors.firstName || ""}
                          disabled={!hasCompanyLicenses}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          label="Last Name"
                          name="lastName"
                          value={branchForm.lastName}
                          onChange={handleBranchFormChange}
                          error={Boolean(branchFormErrors.lastName)}
                          helperText={branchFormErrors.lastName || ""}
                          disabled={!hasCompanyLicenses}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          label="Email"
                          name="email"
                          value={branchForm.email}
                          onChange={handleBranchFormChange}
                          error={Boolean(branchFormErrors.email)}
                          helperText={branchFormErrors.email || ""}
                          disabled={!hasCompanyLicenses}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          label="Phone"
                          name="phone"
                          value={branchForm.phone}
                          onChange={(e) => {
                            const formatted = formatPhone(e);
                            setBranchForm((prev) => ({
                              ...prev,
                              phone: formatted,
                            }));
                          }}
                          error={Boolean(branchFormErrors.phone)}
                          helperText={branchFormErrors.phone || ""}
                          disabled={!hasCompanyLicenses}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Alert severity="info">
                          The branch will use selected company licenses to sign
                          under POA on behalf of the enterprise account.
                        </Alert>
                      </Grid>
                    </Grid>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 3,
                      }}
                    >
                      <LoadingButton
                        variant="contained"
                        loading={createBranchLoading}
                        disabled={
                          !hasCompanyLicenses ||
                          !isBranchFormComplete ||
                          createBranchLoading
                        }
                        onClick={handleCreateBranch}
                      >
                        Create Branch
                      </LoadingButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, lg: 5 }}>
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Company Licenses (Shared)
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      Branch users can sign on behalf of the company under POA
                      using these enterprise licenses.
                    </Typography>

                    {!hasCompanyLicenses ? (
                      <Alert severity="warning">
                        No company licenses are available yet. Add a Company
                        License first to enable branch creation.
                      </Alert>
                    ) : (
                      <>
                        {enterpriseLicenses.length > 1 && (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              flexWrap: "wrap",
                              mb: 2,
                            }}
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={handleSelectAllLicenses}
                            >
                              Select All Licenses
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="inherit"
                              onClick={handleClearLicenseSelection}
                            >
                              Clear Selection
                            </Button>
                          </Box>
                        )}

                        {selectedLicenses.length === 0 ? (
                          <Alert severity="warning">
                            {branchFormErrors.licenses ||
                              "Select at least one company license for this branch."}
                          </Alert>
                        ) : (
                          <>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                width: "100%",
                                mb: 2,
                              }}
                            >
                              <IconButton
                                onClick={() =>
                                  setLicensePage((prev) =>
                                    Math.max(prev - 1, 0),
                                  )
                                }
                                sx={{
                                  visibility:
                                    licensePage === 0 ? "hidden" : "visible",
                                }}
                              >
                                <ArrowBackIosNewIcon
                                  sx={{ fontSize: "20px" }}
                                />
                              </IconButton>

                              <Typography align="center" variant="h6">
                                {licensePage + 1}/{selectedLicenses.length}
                              </Typography>

                              <IconButton
                                onClick={() =>
                                  setLicensePage((prev) =>
                                    Math.min(
                                      prev + 1,
                                      selectedLicenses.length - 1,
                                    ),
                                  )
                                }
                                sx={{
                                  visibility:
                                    licensePage >= selectedLicenses.length - 1
                                      ? "hidden"
                                      : "visible",
                                }}
                              >
                                <ArrowForwardIosIcon
                                  sx={{ fontSize: "20px" }}
                                />
                              </IconButton>
                            </Box>

                            <BranchLicensePreviewCard
                              license={currentSelectedLicense}
                              colorIndex={licensePage + 1}
                              poaName={poaName}
                              poaEmail={branchForm.email}
                              poaPhone={branchForm.phone}
                              selected={selectedLicenseKeys.includes(
                                getLicenseKey(
                                  currentSelectedLicense,
                                  enterpriseLicenses.findIndex(
                                    (license) =>
                                      getLicenseKey(
                                        license,
                                        enterpriseLicenses.indexOf(license),
                                      ) ===
                                      getLicenseKey(
                                        currentSelectedLicense,
                                        enterpriseLicenses.indexOf(
                                          currentSelectedLicense,
                                        ),
                                      ),
                                  ),
                                ),
                              )}
                              onToggle={() => {
                                const currentIndex =
                                  enterpriseLicenses.findIndex(
                                    (license) =>
                                      getLicenseKey(
                                        license,
                                        enterpriseLicenses.indexOf(license),
                                      ) ===
                                      getLicenseKey(
                                        currentSelectedLicense,
                                        enterpriseLicenses.indexOf(
                                          currentSelectedLicense,
                                        ),
                                      ),
                                  );
                                if (currentIndex >= 0) {
                                  handleToggleLicense(
                                    enterpriseLicenses[currentIndex],
                                    currentIndex,
                                  );
                                }
                              }}
                            />
                          </>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        </Card>

        <Drawer
          anchor="right"
          open={branchDetailsOpen}
          onClose={() => setBranchDetailsOpen(false)}
        >
          <Box sx={{ width: { xs: "100vw", sm: 520 }, p: 3 }}>
            <Typography variant="h5" color="primary" gutterBottom>
              {selectedBranch?.branch_name || "Branch Details"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {selectedBranch?.email || ""}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
              <Chip
                label={`Licenses ${selectedBranch?.contractors?.length || 0}`}
                variant="outlined"
              />
              <Chip
                label={`NOC ${selectedBranch?.analytics?.nocCreated ?? 0}`}
                variant="outlined"
              />
              <Chip
                label={`PP ${selectedBranch?.analytics?.ppDownloaded ?? 0}`}
                variant="outlined"
              />
              <Chip
                label={`Downloads ${selectedBranch?.analytics?.availableDownloads ?? "N/A"}`}
                variant="outlined"
              />
            </Box>

            {(selectedBranch?.contractors || []).length === 0 ? (
              <Alert severity="warning">
                This branch does not have any linked company licenses.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {(selectedBranch?.contractors || []).map(
                  (license, licenseIndex) => (
                    <Grid
                      key={`${selectedBranch?.email}-${license?.licenseNumber || licenseIndex}`}
                      size={{ xs: 12 }}
                    >
                      <ContractorCard
                        data={license}
                        colorIndex={licenseIndex}
                        contractorSite={false}
                        currentContractor={{}}
                      />
                    </Grid>
                  ),
                )}
              </Grid>
            )}
          </Box>
        </Drawer>

        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Branch User</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to permanently delete{" "}
              <strong>
                {branchToDelete?.branch_name ||
                  branchToDelete?.email ||
                  "this branch"}
              </strong>
              ? This will also delete the Stripe customer and subscription.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
              Cancel
            </Button>
            <LoadingButton
              color="error"
              variant="contained"
              loading={deleteBranchLoading}
              onClick={handleDeleteBranch}
            >
              Delete Branch
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Grid>
    </Box>
  );
};

export default Branches;
